import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUsers, FaPray, FaMusic, FaBook, FaHeart } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';
import { useData } from '../context/DataContext';
import { useLanguage } from '../context/LanguageContext';

const Main = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(135deg, #ffffff 0%, #f8f5ff 50%, #f0f7ff 100%)'
    : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'};
  color: ${props => props.themeMode === 'light' ? '#2d2d44' : '#fff'};
  overflow-x: hidden;
  transition: all 0.4s ease;
`;

const Header = styled.section`
  text-align: center;
  padding: 6rem 2rem 4rem 2rem;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(135deg, rgba(124, 77, 255, 0.08) 0%, rgba(83, 109, 254, 0.08) 100%)'
    : 'linear-gradient(135deg, rgba(106, 27, 154, 0.3) 0%, rgba(57, 73, 171, 0.3) 100%)'};
  
  @media (max-width: 768px) {
    padding: 4rem 1.5rem 3rem;
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 800;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(90deg, #7c4dff 0%, #536dfe 50%, #7c4dff 100%)'
    : 'linear-gradient(90deg, #b39ddb 0%, #90caf9 50%, #ce93d8 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, 1.3rem);
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#b39ddb'};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.8;
`;

const Section = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(90deg, #7c4dff 0%, #536dfe 100%)'
    : 'linear-gradient(90deg, #b39ddb 0%, #90caf9 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const EventsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const EventCard = styled(motion.div)`
  background: ${props => props.themeMode === 'light'
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(255, 255, 255, 0.05)'};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.15)'
    : 'rgba(179, 157, 219, 0.2)'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.themeMode === 'light'
    ? '0 10px 30px rgba(124, 77, 255, 0.1)'
    : 'none'};
  
  &:hover {
    border-color: #7c4dff;
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(124, 77, 255, 0.2);
  }
`;

const EventImage = styled.div`
  height: 200px;
  background: ${props => props.gradient || 'linear-gradient(135deg, #7c4dff 0%, #536dfe 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: 480px) {
    height: 160px;
    font-size: 3rem;
  }
`;

const EventContent = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const EventTitle = styled.h3`
  font-size: 1.4rem;
  color: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const EventInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#a0a0c0'};
  font-size: 0.95rem;
  svg {
    color: #7c4dff;
    min-width: 20px;
  }
`;

const EventDescription = styled.p`
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#a0a0c0'};
  margin-top: 1rem;
  line-height: 1.6;
`;

const WeeklySection = styled.section`
  padding: 5rem 2rem;
  background: ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.03)'
    : 'rgba(0, 0, 0, 0.2)'};
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const WeeklyGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  
  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

const WeeklyCard = styled(motion.div)`
  background: ${props => props.themeMode === 'light'
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(255, 255, 255, 0.05)'};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.15)'
    : 'rgba(179, 157, 219, 0.2)'};
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: ${props => props.themeMode === 'light'
    ? '0 8px 25px rgba(124, 77, 255, 0.08)'
    : 'none'};
  
  &:hover {
    border-color: #7c4dff;
    background: ${props => props.themeMode === 'light'
      ? 'rgba(124, 77, 255, 0.05)'
      : 'rgba(124, 77, 255, 0.1)'};
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const DayName = styled.h3`
  font-size: 1.3rem;
  color: #7c4dff;
  margin-bottom: 0.5rem;
`;

const ServiceTime = styled.p`
  font-size: 1.1rem;
  color: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
  margin-bottom: 0.5rem;
`;

const ServiceName = styled.p`
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#a0a0c0'};
  font-size: 0.95rem;
`;

const IconCircle = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.themeMode === 'light' ? 'rgba(124, 77, 255, 0.15)' : 'rgba(124, 77, 255, 0.2)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
  color: #7c4dff;
`;

const CTASection = styled.section`
  padding: 5rem 2rem;
  text-align: center;
  background: ${props => props.themeMode === 'light' ? '#f8f9ff' : 'transparent'};
`;

const CTAText = styled(motion.p)`
  font-size: 1.2rem;
  color: ${props => props.themeMode === 'light' ? '#555555' : '#a0a0c0'};
  max-width: 600px;
  margin: 0 auto 2rem;
  line-height: 1.8;
`;

const CTAButton = styled(motion.a)`
  display: inline-block;
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #7c4dff 0%, #536dfe 100%);
  border: none;
  border-radius: 50px;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 8px 30px rgba(124, 77, 255, 0.4);
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(124, 77, 255, 0.6);
  }
`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

// Scroll animation hook
const useScrollAnimation = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);
  
  return { ref, controls };
};

function Events() {
  const { theme } = useTheme();
  const { data } = useData();
  const { t } = useLanguage();
  const themeMode = theme.mode;
  const upcomingAnimation = useScrollAnimation();
  const weeklyAnimation = useScrollAnimation();
  const ctaAnimation = useScrollAnimation();

  // Get events from data context (show actual events from admin)
  const upcomingEvents = data.events || [];

  // Helper function to get icon component
  const getEventIcon = (iconName) => {
    const icons = {
      pray: <FaPray />,
      book: <FaBook />,
      users: <FaUsers />,
      music: <FaMusic />,
      heart: <FaHeart />
    };
    return icons[iconName] || <FaPray />;
  };

  // Get weekly schedule from context or use defaults
  const weeklySchedule = data.weeklySchedule && data.weeklySchedule.length > 0 ? data.weeklySchedule : [
    { id: 1, day: 'Sunday', time: '9:00 AM', title: 'Main Worship Service' },
    { id: 2, day: 'Tuesday', time: '7:00 PM', title: 'Prayer Meeting' },
    { id: 3, day: 'Wednesday', time: '7:00 PM', title: 'Bible Study' },
    { id: 4, day: 'Friday', time: '6:00 PM', title: 'Youth Night' }
  ];

  // Helper to get icon for schedule item
  const getScheduleIcon = (iconName, title) => {
    // If icon is specified in data, use it
    if (iconName) {
      const icons = {
        pray: <FaPray />,
        book: <FaBook />,
        users: <FaUsers />,
        music: <FaMusic />,
        heart: <FaHeart />
      };
      return icons[iconName] || <FaPray />;
    }
    // Otherwise fallback to title-based detection
    const titleLower = title?.toLowerCase() || '';
    if (titleLower.includes('worship') || titleLower.includes('prayer')) return <FaPray />;
    if (titleLower.includes('bible') || titleLower.includes('study')) return <FaBook />;
    if (titleLower.includes('youth') || titleLower.includes('kids')) return <FaUsers />;
    if (titleLower.includes('music') || titleLower.includes('choir')) return <FaMusic />;
    return <FaPray />;
  };

  return (
    <Main themeMode={themeMode}>
      <Header themeMode={themeMode}>
        <Title
          themeMode={themeMode}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {t.events.title}
        </Title>
        <Subtitle
          themeMode={themeMode}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {t.events.subtitle}
        </Subtitle>
      </Header>

      {/* Upcoming Events */}
      <Section>
        <motion.div
          ref={upcomingAnimation.ref}
          initial="hidden"
          animate={upcomingAnimation.controls}
          variants={staggerContainer}
        >
          <SectionTitle themeMode={themeMode} variants={fadeInUp}>📅 Upcoming Events</SectionTitle>
          {upcomingEvents.length === 0 ? (
            <motion.div variants={fadeInUp} style={{ textAlign: 'center', padding: '3rem', color: themeMode === 'dark' ? '#a0a0c0' : '#666' }}>
              <p style={{ fontSize: '1.2rem' }}>No upcoming events at the moment.</p>
              <p style={{ marginTop: '0.5rem' }}>Check back soon for new events!</p>
            </motion.div>
          ) : (
          <EventsGrid variants={staggerContainer}>
            {upcomingEvents.map((event, index) => (
              <EventCard key={event.id || index} themeMode={themeMode} variants={cardVariants}>
                <EventImage gradient={event.gradient || 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)'}>
                  {getEventIcon(event.icon)}
                </EventImage>
                <EventContent>
                  <EventTitle themeMode={themeMode}>{event.title}</EventTitle>
                  <EventInfo>
                    <EventDetail themeMode={themeMode}>
                      <FaCalendarAlt /> {event.date}
                    </EventDetail>
                    <EventDetail themeMode={themeMode}>
                      <FaClock /> {event.time}
                    </EventDetail>
                    <EventDetail themeMode={themeMode}>
                      <FaMapMarkerAlt /> {event.location}
                    </EventDetail>
                  </EventInfo>
                  <EventDescription themeMode={themeMode}>{event.description}</EventDescription>
                </EventContent>
              </EventCard>
            ))}
          </EventsGrid>
          )}
        </motion.div>
      </Section>

      {/* Weekly Schedule */}
      <WeeklySection themeMode={themeMode}>
        <motion.div
          ref={weeklyAnimation.ref}
          initial="hidden"
          animate={weeklyAnimation.controls}
          variants={staggerContainer}
        >
          <SectionTitle themeMode={themeMode} variants={fadeInUp}>🗓️ Weekly Schedule</SectionTitle>
          <WeeklyGrid variants={staggerContainer}>
            {weeklySchedule.map((item, index) => (
              <WeeklyCard key={item.id || index} themeMode={themeMode} variants={cardVariants}>
                <IconCircle themeMode={themeMode}>{getScheduleIcon(item.icon, item.title)}</IconCircle>
                <DayName>{item.day}</DayName>
                <ServiceTime themeMode={themeMode}>{item.time}</ServiceTime>
                <ServiceName themeMode={themeMode}>{item.title}</ServiceName>
              </WeeklyCard>
            ))}
          </WeeklyGrid>
        </motion.div>
      </WeeklySection>

      {/* CTA Section */}
      <CTASection themeMode={themeMode}>
        <motion.div
          ref={ctaAnimation.ref}
          initial="hidden"
          animate={ctaAnimation.controls}
          variants={staggerContainer}
        >
          <SectionTitle themeMode={themeMode} variants={fadeInUp}>🙏 Join Us This Week</SectionTitle>
          <CTAText themeMode={themeMode} variants={fadeInUp}>
            We would love to have you worship with us! Whether you're new to faith 
            or looking for a church home, you're welcome here.
          </CTAText>
          <motion.div variants={fadeInUp}>
            <CTAButton
              href="/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Directions
            </CTAButton>
          </motion.div>
        </motion.div>
      </CTASection>
    </Main>
  );
}

export default Events;
